import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { patientAuthApi } from '../api';
import { clearToken } from '../../../api/patientSession';
import { govPageContainer, GOV_TYPE } from '../ui/PatientUIGovernor';

/**
 * Real password change against POST /auth/change-password. On success the
 * backend revokes every session (tokenVersion bump), so we clear the local
 * token and send the user back to login — anything else would leave a
 * "changed" password with stale sessions still alive.
 */
export default function PatientChangePassword() {
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const handleSubmit = async () => {
    setError('');
    if (newPassword.length < 8) { setError('The new password must be at least 8 characters.'); return; }
    if (newPassword !== confirm) { setError('The new passwords do not match.'); return; }
    setBusy(true);
    try {
      await patientAuthApi.changePassword({
        currentPassword: currentPassword || undefined,
        newPassword,
      });
      setDone(true);
      clearToken();
      setTimeout(() => navigate('/patient/entry'), 2500);
    } catch (e: any) {
      const fieldMsg = e?.body?.errors?.[0]?.message;
      setError(fieldMsg || e?.body?.message || 'Password could not be changed — check your current password and connection.');
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div style={govPageContainer()} className="max-w-md mx-auto text-center py-16">
        <div className="text-4xl mb-4">✅</div>
        <h1 className="text-lg font-bold text-gray-900">Password changed</h1>
        <p className="text-sm text-gray-500 mt-2">
          All sessions have been signed out for your security. Redirecting you to login…
        </p>
      </div>
    );
  }

  return (
    <div style={govPageContainer()} className="max-w-md mx-auto space-y-4">
      <h1 style={GOV_TYPE.pageTitle}>Change Password</h1>
      <p className="text-xs text-gray-500">
        Changing your password signs you out of every device, including this one.
      </p>

      <div>
        <label className="text-xs text-gray-500 mb-1 block">Current Password</label>
        <input type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)}
          placeholder="Leave blank if you never set one"
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30" />
      </div>
      <div>
        <label className="text-xs text-gray-500 mb-1 block">New Password</label>
        <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)}
          placeholder="At least 8 characters"
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30" />
      </div>
      <div>
        <label className="text-xs text-gray-500 mb-1 block">Confirm New Password</label>
        <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)}
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30" />
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-700 font-medium">{error}</div>
      )}

      <button onClick={handleSubmit} disabled={busy || !newPassword}
        className="w-full py-2.5 bg-teal-600 text-white text-sm font-semibold rounded-lg hover:bg-teal-700 disabled:opacity-40">
        {busy ? 'Changing…' : 'Change Password'}
      </button>
    </div>
  );
}
